import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { ADMIN_GET_PRODUCT, ADMIN_GET_TAG_LIST, GET_PREFERENCE_VARIANT, ADMIN_UPDATE_PRODUCT } from '../graphql/queries';

// ── Shared sub-components ────────────────────────────────────────

function Toast({ message, type, onClose }) {
  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
      background: type === 'error' ? '#f43f5e' : '#10b981',
      color: '#fff', borderRadius: 10, padding: '12px 20px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.2)', fontSize: 14, fontWeight: 500,
      display: 'flex', alignItems: 'center', gap: 12,
    }}>
      {message}
      <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 0 }}>×</button>
    </div>
  );
}

function SectionCard({ title, icon, children }) {
  return (
    <div style={{
      background: 'var(--white)', borderRadius: 14, boxShadow: 'var(--shadow)',
      padding: '22px 24px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 9, background: 'rgba(99,102,241,0.1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <svg style={{ width: 16, height: 16, color: 'var(--primary)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            {icon}
          </svg>
        </div>
        <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--slate-800)' }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--slate-500)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '10px 14px', border: '1.5px solid var(--slate-200)',
  borderRadius: 10, fontSize: 14, color: 'var(--slate-800)', background: 'var(--white)',
  outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s',
};

const readonlyInputStyle = {
  ...inputStyle, background: 'var(--slate-50)', color: 'var(--slate-400)', cursor: 'default',
};

// ── Images section ────────────────────────────────────────────────

function ImagesSection({ imageUrls }) {
  const [activeIdx, setActiveIdx] = useState(0);

  if (!imageUrls || imageUrls.length === 0) {
    return (
      <SectionCard title="Images" icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />}>
        <div style={{
          height: 200, background: 'var(--slate-50)', borderRadius: 12,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}>
          <svg style={{ width: 40, height: 40, color: 'var(--slate-300)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span style={{ fontSize: 13, color: 'var(--slate-400)' }}>No images uploaded</span>
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard title="Images" icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />}>
      {/* Main image */}
      <div style={{ borderRadius: 12, overflow: 'hidden', background: 'var(--slate-50)', marginBottom: 12 }}>
        <img
          src={imageUrls[activeIdx]}
          alt="Product"
          style={{ width: '100%', height: 260, objectFit: 'contain' }}
          onError={e => { e.target.style.display = 'none'; }}
        />
      </div>

      {/* Thumbnails */}
      {imageUrls.length > 1 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {imageUrls.map((url, i) => (
            <button
              key={i}
              onClick={() => setActiveIdx(i)}
              style={{
                width: 60, height: 60, borderRadius: 8, overflow: 'hidden', padding: 0,
                border: `2px solid ${i === activeIdx ? 'var(--primary)' : 'var(--slate-200)'}`,
                cursor: 'pointer', background: 'var(--slate-50)', flexShrink: 0,
                transition: 'border-color 0.15s',
              }}
            >
              <img src={url} alt={`img-${i}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </button>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

// ── Status chip ────────────────────────────────────────────────────

function StatusChip({ label, value, selected, onSelect }) {
  return (
    <button
      onClick={() => onSelect(value)}
      style={{
        padding: '9px 20px', borderRadius: 20, fontSize: 13, fontWeight: 600,
        border: `1.5px solid ${selected ? 'var(--primary)' : 'var(--slate-200)'}`,
        background: selected ? 'rgba(99,102,241,0.1)' : 'var(--white)',
        color: selected ? 'var(--primary)' : 'var(--slate-500)',
        cursor: 'pointer', transition: 'all 0.15s',
      }}
    >
      {label}
    </button>
  );
}

// ── Variant chip ───────────────────────────────────────────────────

function VariantChip({ label, selected, readonly, onToggle }) {
  return (
    <button
      onClick={readonly ? undefined : onToggle}
      style={{
        padding: '7px 14px', borderRadius: 20, fontSize: 13, fontWeight: 500,
        border: `1.5px solid ${readonly ? 'var(--slate-200)' : selected ? 'var(--primary)' : 'var(--slate-200)'}`,
        background: readonly ? 'var(--slate-50)' : selected ? 'rgba(99,102,241,0.1)' : 'var(--white)',
        color: readonly ? 'var(--slate-400)' : selected ? 'var(--primary)' : 'var(--slate-600)',
        cursor: readonly ? 'default' : 'pointer', transition: 'all 0.15s',
      }}
    >
      {label}
    </button>
  );
}

// ── Main page ──────────────────────────────────────────────────────

export default function EditProduct() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [sellingPrice, setSellingPrice] = useState('');
  const [mrp, setMrp] = useState('');
  const [discount, setDiscount] = useState('');
  const [status, setStatus] = useState('active');
  const [selectedTagIds, setSelectedTagIds] = useState(new Set());
  const [selectedNewVariantIds, setSelectedNewVariantIds] = useState(new Set());
  const [initialized, setInitialized] = useState(false);
  const [toast, setToast] = useState(null);

  const TAGS_PAGE_SIZE = 20;

  const { data: productData, loading: productLoading, error: productError } = useQuery(ADMIN_GET_PRODUCT, {
    variables: { id },
    fetchPolicy: 'network-only',
  });
  const { data: tagsData, fetchMore: fetchMoreTags, networkStatus: tagsNetworkStatus } = useQuery(ADMIN_GET_TAG_LIST, {
    variables: { cursor: null, limit: TAGS_PAGE_SIZE },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const { data: variantData } = useQuery(GET_PREFERENCE_VARIANT, { fetchPolicy: 'network-only' });
  const [updateProduct, { loading: saving }] = useMutation(ADMIN_UPDATE_PRODUCT);

  const product = productData?.adminGetProduct?.product;
  const loadedTags = tagsData?.adminGetTagList?.tags || [];
  const tagsHasMore = !!tagsData?.adminGetTagList?.hasMore;
  const tagsNextCursor = tagsData?.adminGetTagList?.nextCursor ?? null;
  const tagsLoadingMore = tagsNetworkStatus === 3; // NetworkStatus.fetchMore
  const preferenceVariant = variantData?.getPreferenceVariant?.setting;

  // Merge: loaded tags + any pre-selected product tags not yet in loaded pages
  const loadedTagIds = new Set(loadedTags.map(t => t.id));
  const extraSelectedTags = (product?.tags || []).filter(t => selectedTagIds.has(t.id) && !loadedTagIds.has(t.id));
  const displayTags = [...extraSelectedTags, ...loadedTags];

  const handleLoadMoreTags = () => {
    if (!tagsHasMore || !tagsNextCursor) return;
    fetchMoreTags({
      variables: { cursor: tagsNextCursor, limit: TAGS_PAGE_SIZE },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.adminGetTagList) return prev;
        const prevTags = prev?.adminGetTagList?.tags || [];
        const newTags = fetchMoreResult.adminGetTagList.tags || [];
        const seen = new Set(prevTags.map(t => t.id));
        const merged = [...prevTags, ...newTags.filter(t => !seen.has(t.id))];
        return {
          adminGetTagList: {
            ...fetchMoreResult.adminGetTagList,
            tags: merged,
          },
        };
      },
    });
  };

  // Populate form once product is loaded
  useEffect(() => {
    if (product && !initialized) {
      setTitle(product.title);
      setDescription(product.description);
      setSellingPrice(String(product.sellingPrice));
      setMrp(String(product.mrp));
      setDiscount(String(product.discount));
      setStatus(product.status);
      setSelectedTagIds(new Set((product.tags || []).map(t => t.id)));
      setInitialized(true);
    }
  }, [product, initialized]);

  // Auto-calc discount
  useEffect(() => {
    const price = parseFloat(sellingPrice) || 0;
    const mrpVal = parseFloat(mrp) || 0;
    if (mrpVal > 0 && price > 0 && mrpVal >= price) {
      setDiscount(((mrpVal - price) / mrpVal * 100).toFixed(1));
    } else {
      setDiscount('');
    }
  }, [sellingPrice, mrp]);

  const showToast = (message, type) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const handleSave = async () => {
    if (!title.trim()) return showToast('Product title is required', 'error');
    const price = parseFloat(sellingPrice);
    const mrpVal = parseFloat(mrp);
    if (!sellingPrice || isNaN(price)) return showToast('Valid selling price is required', 'error');
    if (!mrp || isNaN(mrpVal)) return showToast('Valid MRP is required', 'error');
    if (mrpVal < price) return showToast('MRP must be ≥ selling price', 'error');

    const tagLookup = new Map();
    (product?.tags || []).forEach(t => tagLookup.set(t.id, { id: t.id, name: t.name }));
    displayTags.forEach(t => tagLookup.set(t.id, { id: t.id, name: t.name }));
    const tags = Array.from(selectedTagIds).map(id => tagLookup.get(id)).filter(Boolean);
    const addVariants = (preferenceVariant?.fieldValues || [])
      .filter(fv => selectedNewVariantIds.has(fv.id))
      .map(fv => ({ id: fv.id, value: fv.value }));

    try {
      const { data } = await updateProduct({
        variables: {
          id: id,
          title: title.trim(),
          description: description.trim(),
          sellingPrice: price,
          mrp: mrpVal,
          discount: parseFloat(discount) || 0,
          status,
          tags,
          addVariants,
        },
      });

      if (data?.adminUpdateProduct?.error) {
        showToast(data.adminUpdateProduct.message || 'Update failed', 'error');
      } else {
        showToast('Product updated successfully!', 'success');
        setTimeout(() => navigate('/products'), 1200);
      }
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  const existingVariantIds = new Set((product?.variants || []).map(v => v.id));
  const addableVariants = (preferenceVariant?.fieldValues || []).filter(fv => !existingVariantIds.has(fv.id));

  // ── Loading state ─────────────────────────────────────────────
  if (productLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <div style={{ fontSize: 14, color: 'var(--slate-400)' }}>Loading product...</div>
      </div>
    );
  }

  if (productError || productData?.adminGetProduct?.error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 12 }}>
        <div style={{ fontSize: 14, color: '#f43f5e' }}>
          {productError?.message || productData?.adminGetProduct?.message || 'Failed to load product'}
        </div>
        <button
          onClick={() => navigate('/products')}
          style={{ color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}
        >
          ← Back to products
        </button>
      </div>
    );
  }

  // ── Page ─────────────────────────────────────────────────────
  return (
    <div>
      {/* Header */}
      <header className="dashboard-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button
            onClick={() => navigate('/products')}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
              border: '1.5px solid var(--slate-200)', borderRadius: 10, background: 'var(--white)',
              color: 'var(--slate-600)', cursor: 'pointer', fontSize: 13, fontWeight: 500,
              transition: 'all 0.15s', flexShrink: 0,
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.color = 'var(--primary)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--slate-200)'; e.currentTarget.style.color = 'var(--slate-600)'; }}
          >
            <svg style={{ width: 15, height: 15 }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Products
          </button>
          <div>
            <h1 className="dashboard-title">Edit Product</h1>
            <p className="dashboard-subtitle" style={{ maxWidth: 340, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {product?.title}
            </p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '10px 22px',
            background: saving ? 'var(--slate-400)' : 'var(--primary)',
            color: '#fff', border: 'none', borderRadius: 10,
            fontSize: 14, fontWeight: 600, cursor: saving ? 'default' : 'pointer',
            transition: 'background 0.15s', flexShrink: 0,
          }}
        >
          <svg style={{ width: 16, height: 16 }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
          </svg>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </header>

      {/* Two-column layout */}
      <main className="dashboard-main">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20, alignItems: 'start' }}>
          {/* Left column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Images */}
            <ImagesSection imageUrls={product?.imageUrls || []} />

            {/* Basic Info */}
            <SectionCard
              title="Basic Information"
              icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />}
            >
              <Field label="Product Title *">
                <input
                  style={inputStyle}
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="Enter product title"
                  onFocus={e => (e.target.style.borderColor = 'var(--primary)')}
                  onBlur={e => (e.target.style.borderColor = 'var(--slate-200)')}
                />
              </Field>
              <Field label="Description *">
                <textarea
                  style={{ ...inputStyle, minHeight: 100, resize: 'vertical' }}
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Enter product description"
                  onFocus={e => (e.target.style.borderColor = 'var(--primary)')}
                  onBlur={e => (e.target.style.borderColor = 'var(--slate-200)')}
                />
              </Field>
            </SectionCard>

            {/* Pricing */}
            <SectionCard
              title="Pricing"
              icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />}
            >
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <Field label="Selling Price (₹) *">
                  <input
                    style={inputStyle}
                    type="number"
                    min="0"
                    value={sellingPrice}
                    onChange={e => setSellingPrice(e.target.value)}
                    placeholder="0"
                    onFocus={e => (e.target.style.borderColor = 'var(--primary)')}
                    onBlur={e => (e.target.style.borderColor = 'var(--slate-200)')}
                  />
                </Field>
                <Field label="MRP (₹) *">
                  <input
                    style={inputStyle}
                    type="number"
                    min="0"
                    value={mrp}
                    onChange={e => setMrp(e.target.value)}
                    placeholder="0"
                    onFocus={e => (e.target.style.borderColor = 'var(--primary)')}
                    onBlur={e => (e.target.style.borderColor = 'var(--slate-200)')}
                  />
                </Field>
              </div>
              <Field label="Discount (%) — auto calculated">
                <input
                  style={readonlyInputStyle}
                  value={discount ? `${discount}%` : '—'}
                  readOnly
                />
              </Field>
            </SectionCard>

            {/* Variants */}
            <SectionCard
              title={preferenceVariant?.attributeName || (product?.variants?.length > 0 ? 'Variants' : 'Variants')}
              icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />}
            >
              {/* Existing variants — table */}
              {product?.variants?.length > 0 ? (
                <div style={{ marginBottom: addableVariants.length > 0 ? 24 : 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate-400)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Current variants
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--slate-400)', background: 'var(--slate-100)', padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>
                      {product.variants.length} total
                    </span>
                  </div>
                  <div style={{ border: '1px solid var(--slate-100)', borderRadius: 10, overflow: 'hidden' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '36px 1fr auto', background: 'var(--slate-50)', padding: '8px 14px', borderBottom: '1px solid var(--slate-100)' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate-400)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>#</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate-400)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Value</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate-400)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Status</span>
                    </div>
                    {product.variants.map((v, i) => (
                      <div
                        key={v.id}
                        style={{
                          display: 'grid', gridTemplateColumns: '36px 1fr auto',
                          padding: '11px 14px', alignItems: 'center',
                          borderBottom: i < product.variants.length - 1 ? '1px solid var(--slate-100)' : 'none',
                          background: 'var(--white)',
                        }}
                      >
                        <span style={{ fontSize: 12, color: 'var(--slate-400)', fontWeight: 600 }}>{i + 1}</span>
                        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--slate-700)' }}>{v.value}</span>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          fontSize: 11, fontWeight: 600, color: 'var(--slate-400)',
                          background: 'var(--slate-100)', padding: '3px 8px', borderRadius: 20,
                        }}>
                          <svg style={{ width: 10, height: 10 }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          Fixed
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div style={{ padding: '16px', textAlign: 'center', color: 'var(--slate-400)', fontSize: 13, background: 'var(--slate-50)', borderRadius: 10, marginBottom: addableVariants.length > 0 ? 20 : 0 }}>
                  No variants added yet
                </div>
              )}

              {/* Add new variants */}
              {addableVariants.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate-400)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
                    Add new variants
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {addableVariants.map(fv => {
                      const selected = selectedNewVariantIds.has(fv.id);
                      return (
                        <VariantChip
                          key={fv.id}
                          label={fv.value}
                          selected={selected}
                          onToggle={() => setSelectedNewVariantIds(prev => {
                            const next = new Set(prev);
                            selected ? next.delete(fv.id) : next.add(fv.id);
                            return next;
                          })}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {!preferenceVariant && product?.variants?.length === 0 && (
                <div style={{ fontSize: 13, color: 'var(--slate-400)' }}>No variant type configured in settings</div>
              )}
              {preferenceVariant && addableVariants.length === 0 && product?.variants?.length > 0 && (
                <div style={{ fontSize: 13, color: 'var(--slate-400)', marginTop: 4 }}>All available variants have been added</div>
              )}
            </SectionCard>
          </div>

          {/* Right column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Status */}
            <SectionCard
              title="Status"
              icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />}
            >
              <div style={{ display: 'flex', gap: 8 }}>
                <StatusChip label="Active" value="active" selected={status === 'active'} onSelect={setStatus} />
                <StatusChip label="Draft" value="draft" selected={status === 'draft'} onSelect={setStatus} />
              </div>
              <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 10, background: status === 'active' ? 'rgba(16,185,129,0.07)' : 'rgba(100,116,139,0.07)' }}>
                <span style={{ fontSize: 12, color: status === 'active' ? '#059669' : 'var(--slate-400)', fontWeight: 500 }}>
                  {status === 'active' ? '✓ Visible to customers in the app' : '◦ Hidden — save as work in progress'}
                </span>
              </div>
            </SectionCard>

            {/* Tags */}
            <SectionCard
              title="Tags"
              icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />}
            >
              {displayTags.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--slate-400)' }}>No tags available</div>
              ) : (
                <>
                  <div style={{
                    display: 'flex', flexWrap: 'wrap', gap: 8,
                    maxHeight: 220, overflowY: 'auto', paddingRight: 4,
                  }}>
                    {displayTags.map(tag => {
                      const selected = selectedTagIds.has(tag.id);
                      return (
                        <button
                          key={tag.id}
                          onClick={() => setSelectedTagIds(prev => {
                            const next = new Set(prev);
                            selected ? next.delete(tag.id) : next.add(tag.id);
                            return next;
                          })}
                          style={{
                            padding: '7px 14px', borderRadius: 20, fontSize: 13, fontWeight: 500,
                            border: `1.5px solid ${selected ? 'var(--primary)' : 'var(--slate-200)'}`,
                            background: selected ? 'rgba(99,102,241,0.1)' : 'var(--white)',
                            color: selected ? 'var(--primary)' : 'var(--slate-500)',
                            cursor: 'pointer', transition: 'all 0.15s',
                          }}
                        >
                          {selected && <span style={{ marginRight: 5 }}>✓</span>}
                          {tag.name}
                        </button>
                      );
                    })}
                  </div>
                  {tagsHasMore && (
                    <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center' }}>
                      <button
                        onClick={handleLoadMoreTags}
                        disabled={tagsLoadingMore}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          padding: '8px 16px', borderRadius: 20, fontSize: 13, fontWeight: 500,
                          border: '1.5px solid var(--slate-200)',
                          background: 'var(--white)',
                          color: tagsLoadingMore ? 'var(--slate-400)' : 'var(--primary)',
                          cursor: tagsLoadingMore ? 'default' : 'pointer', transition: 'all 0.15s',
                        }}
                      >
                        {tagsLoadingMore ? 'Loading...' : 'Load more'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </SectionCard>

            {/* Read-only info */}
            <SectionCard
              title="Product Info"
              icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />}
            >
              {[
                { label: 'Product ID', value: product?.id },
                { label: 'Quantity', value: product?.quantity != null ? String(product.quantity) : '—' },
                { label: 'Images', value: product?.imageUrls?.length ? `${product.imageUrls.length} image${product.imageUrls.length !== 1 ? 's' : ''}` : 'None' },
              ].map(item => (
                <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid var(--slate-100)' }}>
                  <span style={{ fontSize: 12, color: 'var(--slate-400)', fontWeight: 500 }}>{item.label}</span>
                  <span style={{ fontSize: item.label === 'Product ID' ? 11 : 13, color: 'var(--slate-700)', fontWeight: 500, fontFamily: item.label === 'Product ID' ? 'monospace' : 'inherit' }}>
                    {item.value}
                  </span>
                </div>
              ))}
            </SectionCard>
          </div>
        </div>
      </main>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
